import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subscription } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { passwordStrengthValidator, passwordCrossFieldValidator } from '@edtech-ui/core/utils/password-validators';
import { emailOrPhoneValidator } from '@edtech-ui/core/utils/identifier-validator';
import { COURSES_LIST } from '@edtech-ui/core/constants/education-master-data';
import { COLLEGE_BRANCHES_LIST, PLACEMENT_INDUSTRY_TYPES } from '@edtech-ui/core/constants/college-constants/college-options.constants';
import { CollegeItem } from '@edtech-ui/core/services/institution.service';
import { CantFindCollegeModalComponent } from '../../shared/cant-find-college-modal/cant-find-college-modal.component';
import { CollegeAutocompleteComponent } from '../../shared/college-autocomplete/college-autocomplete.component';
import { CollegePortalService } from '../services/college-portal.service';
import { phoneValidator } from '../../hiring-workspace/recruiter-auth/recruiter-auth.component';

@Component({
	selector: 'app-college-auth',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, RouterModule, MatDialogModule, CollegeAutocompleteComponent],
	templateUrl: './college-auth.component.html',
	styleUrls: ['./college-auth.component.scss']
})
export class CollegeAuthComponent implements OnInit, OnDestroy {
	isLogin = true;
	isLoading = false;
	showPassword = false;
	showConfirmPassword = false;

	loginForm!: FormGroup;
	registerForm!: FormGroup;

	// Login with OTP
	loginMethod: 'password' | 'otp' = 'password';
	isVerifyingOtp = false;
	otpTarget = '';

	// Registration OTP verification (distinct from the login-OTP state above)
	isVerifyingRegistrationOtp = false;
	registrationOtpTarget = '';
	registrationOtpControl = new FormControl('', [Validators.required, Validators.pattern('^[0-9]{6}$')]);
	private pendingRegisterFormData: FormData | null = null;

	// Forgot Password Flow
	forgotPasswordStep = 0; // 0 = not in flow, 1 = Account, 2 = Verify, 3 = Reset, 4 = Success
	forgotPasswordAccountForm!: FormGroup;
	forgotPasswordResetForm!: FormGroup;
	forgotPasswordEmailOrPhone = '';
	forgotPasswordOtpValues: string[] = new Array(6).fill('');
	forgotPasswordResetToken = '';
	showFpPassword = false;
	showFpConfirmPassword = false;
	isResendingOtp = false;
	resendCountdown = 0;
	private resendInterval: any;

	// Streams (degree programs) — reuses the same list already used for student registration
	streamOptions = COURSES_LIST;
	streams: string[] = [];
	filteredStreams: string[] = [];
	showStreamsDropdown = false;

	// Courses offered (branches/departments)
	branchOptions = COLLEGE_BRANCHES_LIST;
	coursesOffered: string[] = [];
	filteredBranches: string[] = [];
	showBranchesDropdown = false;

	// Placement industries — mirrors the backend's IndustryType taxonomy (shared with companies)
	industryOptions = PLACEMENT_INDUSTRY_TYPES;
	placementIndustries: string[] = [];
	filteredIndustries: string[] = [];
	showIndustriesDropdown = false;

	selectedLogoFile: File | null = null;
	logoPreview: string | null = null;

	// College name search-and-select (same pattern used for student registration):
	// typed text is never trusted as a valid college on its own — it must come
	// from selecting a search result, or from the "Can't find your college?" modal.
	selectedInstitutionId: string | null = null;

	private subscription = new Subscription();

	constructor(
		private fb: FormBuilder,
		private router: Router,
		private toastr: ToastrService,
		private _authService: AuthServiceService,
		private _collegePortalService: CollegePortalService,
		private _dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.initForms();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		if (this.resendInterval) clearInterval(this.resendInterval);
	}

	private initForms(): void {
		this.loginForm = this.fb.group({
			identifier: ['', [Validators.required, emailOrPhoneValidator]],
			password: [''],
			otp: ['']
		});
		this.setLoginValidators();

		this.forgotPasswordAccountForm = this.fb.group({
			identifier: ['', [Validators.required, Validators.email]]
		});

		this.forgotPasswordResetForm = this.fb.group({
			password: ['', [Validators.required, Validators.minLength(8), passwordStrengthValidator]],
			confirm_password: ['', Validators.required]
		}, { validators: passwordCrossFieldValidator });

		this.registerForm = this.fb.group({
			college_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
			college_url: ['', [Validators.required, Validators.pattern('^(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)$')]],
			university_name: [''],
			ranking: [''],
			city: [''],
			state: [''],
			country: ['India'],
			point_of_contact_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(255), Validators.pattern('^[a-zA-Z\\s.]+$')]],
			point_of_contact_email: ['', [Validators.required, Validators.email]],
			phone_number: ['', [Validators.required, phoneValidator()]],
			password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(32), passwordStrengthValidator]],
			confirm_password: ['', [Validators.required]]
		}, { validators: passwordCrossFieldValidator });
	}

	toggleAuth(type: 'login' | 'signup'): void {
		this.isLogin = type === 'login';
		if (this.isLogin) {
			this.isVerifyingOtp = false;
			this.setLoginValidators();
		}
	}

	togglePasswordVisibility(): void {
		this.showPassword = !this.showPassword;
	}

	toggleConfirmPasswordVisibility(): void {
		this.showConfirmPassword = !this.showConfirmPassword;
	}

	// ─────────────────────────────────────────────
	// Login with OTP
	// ─────────────────────────────────────────────

	setLoginValidators(): void {
		const identifierControl = this.loginForm.get('identifier');
		const passwordControl = this.loginForm.get('password');
		const otpControl = this.loginForm.get('otp');

		identifierControl?.clearValidators();
		passwordControl?.clearValidators();
		otpControl?.clearValidators();

		if (this.isVerifyingOtp) {
			otpControl?.setValidators([Validators.required, Validators.pattern('^[0-9]{6}$')]);
		} else {
			identifierControl?.setValidators([Validators.required, emailOrPhoneValidator]);
			if (this.loginMethod === 'password') {
				passwordControl?.setValidators([Validators.required]);
			}
		}

		identifierControl?.updateValueAndValidity();
		passwordControl?.updateValueAndValidity();
		otpControl?.updateValueAndValidity();
	}

	switchLoginMethod(method: 'password' | 'otp'): void {
		this.loginMethod = method;
		this.isVerifyingOtp = false;
		this.setLoginValidators();
	}

	sendOtp(isResend: boolean = false): void {
		if (!isResend && this.loginForm.invalid) {
			this.loginForm.markAllAsTouched();
			return;
		}

		const identifier = this.loginForm.value.identifier;
		const isPhone = /^[6-9]\d{9}$/.test(identifier) || /^\d+$/.test(identifier);
		const payload: any = {
			is_default: false,
			login_source: 'web',
			fcm_token: ''
		};
		if (isPhone) {
			payload.phone = identifier;
		} else {
			payload.email = identifier;
		}
		this.otpTarget = identifier;

		this.isLoading = true;
		this._authService.postLogin(payload).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (response) => {
				if (response.Success) {
					this.isVerifyingOtp = true;
					this.setLoginValidators();
					this.toastr.success(isResend ? 'OTP resent successfully' : 'OTP sent successfully');
				} else {
					this.toastr.error(response.Message);
				}
			},
			error: () => {
				this.toastr.error(isResend ? 'Failed to resend OTP' : 'Failed to send OTP');
			}
		});
	}

	changeOtpTarget(): void {
		this.isVerifyingOtp = false;
		const otpControl = this.loginForm.get('otp');
		if (otpControl) {
			otpControl.setValue('');
			otpControl.markAsUntouched();
			otpControl.markAsPristine();
		}
		this.setLoginValidators();
	}

	resendOtp(): void {
		const otpControl = this.loginForm.get('otp');
		if (otpControl) {
			otpControl.setValue('');
			otpControl.markAsUntouched();
			otpControl.markAsPristine();
		}
		this.sendOtp(true);
	}

	// ─────────────────────────────────────────────
	// Forgot Password Flow
	// ─────────────────────────────────────────────

	goToForgotPassword(): void {
		this.forgotPasswordStep = 1;
		this.forgotPasswordAccountForm.reset();
		this.forgotPasswordEmailOrPhone = '';
	}

	forgotPasswordBack(): void {
		if (this.forgotPasswordStep > 1 && this.forgotPasswordStep < 4) {
			this.forgotPasswordStep--;
		} else {
			this.forgotPasswordStep = 0;
		}
	}

	startResendTimer(): void {
		this.resendCountdown = 30;
		if (this.resendInterval) clearInterval(this.resendInterval);
		this.resendInterval = setInterval(() => {
			this.resendCountdown--;
			if (this.resendCountdown <= 0) {
				clearInterval(this.resendInterval);
			}
		}, 1000);
	}

	submitForgotPasswordAccount(): void {
		if (this.forgotPasswordAccountForm.invalid) {
			this.forgotPasswordAccountForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;
		const identifier = this.forgotPasswordAccountForm.value.identifier;
		const payload = { phone_email: identifier, fcm_token: '' };

		this._authService.postForgotPassword(payload).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (response: any) => {
				if (response.Success) {
					this.toastr.success(response.Message);
					this.forgotPasswordEmailOrPhone = response.Data?.Email || response.Data?.phone_email || identifier;
					this.forgotPasswordOtpValues.fill('');
					this.forgotPasswordStep = 2;
					this.startResendTimer();
				} else {
					this.toastr.error(response.Message);
				}
			},
			error: () => {
				this.toastr.error('Something went wrong while sending OTP.');
			}
		});
	}

	trackByIndex(index: number): number {
		return index;
	}

	handleFpOtpInput(event: any, index: number): void {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		if (!/^\d$/.test(value)) {
			input.value = '';
			this.forgotPasswordOtpValues[index] = '';
			return;
		}

		this.forgotPasswordOtpValues[index] = value;

		if (value && index < 5) {
			const nextInput = input.nextElementSibling as HTMLInputElement;
			nextInput?.focus();
		}
	}

	handleFpKeyDown(event: KeyboardEvent, index: number): void {
		const target = event.target as HTMLInputElement;
		if (event.key === 'Backspace' && !this.forgotPasswordOtpValues[index] && index > 0) {
			const prevInput = target.previousElementSibling as HTMLInputElement;
			prevInput?.focus();
		}
	}

	handleFpOtpPaste(event: ClipboardEvent): void {
		event.preventDefault();
		const clipboardData = event.clipboardData || (window as any).clipboardData;
		const pastedText = clipboardData?.getData('text');
		if (!pastedText) return;

		const numbers = pastedText.replace(/\D/g, '').substring(0, 6);
		for (let i = 0; i < numbers.length; i++) {
			this.forgotPasswordOtpValues[i] = numbers[i];
		}

		setTimeout(() => {
			const inputs = document.querySelectorAll('.fp-otp-inputs input') as NodeListOf<HTMLInputElement>;
			if (inputs && inputs.length > 0) {
				const focusIndex = Math.min(numbers.length, 5);
				inputs[focusIndex].focus();
			}
		}, 10);
	}

	isFpOtpFilled(): boolean {
		return this.forgotPasswordOtpValues.join('').length === 6;
	}

	getFpOtpCode(): string {
		return this.forgotPasswordOtpValues.join('');
	}

	resendFpOtp(): void {
		if (this.resendCountdown > 0) return;
		this.isResendingOtp = true;
		this._authService.postResendOtp(this.forgotPasswordEmailOrPhone).pipe(
			finalize(() => { this.isResendingOtp = false; })
		).subscribe({
			next: (response: any) => {
				if (response?.Success) {
					this.toastr.success(response.Message || 'OTP sent successfully');
					this.startResendTimer();
				} else {
					this.toastr.error(response.Message || 'Failed to resend OTP');
				}
			},
			error: () => {
				this.toastr.error('Something went wrong while resending OTP');
			}
		});
	}

	submitForgotPasswordVerify(): void {
		const otp = this.getFpOtpCode();
		if (!(otp.length === 6 && /^\d{6}$/.test(otp))) {
			this.toastr.error('Invalid OTP format');
			return;
		}

		this.isLoading = true;
		const data = { email: this.forgotPasswordEmailOrPhone, otp: Number(otp) };
		this._authService.postVerifyResetOtp(data).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (response: any) => {
				if (response?.Success) {
					this.toastr.success('OTP Verified');
					this.forgotPasswordResetToken = response.Data.reset_token;
					this.forgotPasswordResetForm.reset();
					this.forgotPasswordStep = 3;
				} else {
					this.toastr.error(response.Message || 'Invalid OTP');
					this.forgotPasswordOtpValues.fill('');
					setTimeout(() => {
						const firstInput = document.getElementById('college-fp-otp-input-0') as HTMLInputElement;
						if (firstInput) firstInput.focus();
					}, 0);
				}
			},
			error: () => {
				this.toastr.error('Verification failed. Please try again.');
				this.forgotPasswordOtpValues.fill('');
				setTimeout(() => {
					const firstInput = document.getElementById('college-fp-otp-input-0') as HTMLInputElement;
					if (firstInput) firstInput.focus();
				}, 0);
			}
		});
	}

	submitForgotPasswordReset(): void {
		if (this.forgotPasswordResetForm.invalid) {
			this.forgotPasswordResetForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;
		const payload = {
			new_password: this.forgotPasswordResetForm.value.password,
			email: this.forgotPasswordEmailOrPhone,
			reset_token: this.forgotPasswordResetToken
		};

		this._authService.postSetNewPassword(payload).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (response: any) => {
				if (response.Success) {
					this.toastr.success(response.Message);
					this.forgotPasswordStep = 4;
				} else {
					this.toastr.error(response.Message);
				}
			},
			error: () => {
				this.toastr.error('Something went wrong while resetting password.');
			}
		});
	}

	// ─────────────────────────────────────────────
	// Multi-select chip inputs (streams / branches / industries)
	// Same hand-rolled type-ahead + chip-list pattern used for company's
	// office_locations (there is no reusable multi-select component in this app).
	// ─────────────────────────────────────────────

	private filterOptions(options: string[], query: string): string[] {
		const q = query.trim().toLowerCase();
		if (!q) return [];
		return options.filter(o => o.toLowerCase().includes(q));
	}

	onStreamInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.filteredStreams = this.filterOptions(this.streamOptions, value);
		this.showStreamsDropdown = this.filteredStreams.length > 0;
	}

	addStream(value: string): void {
		const match = this.streamOptions.find(o => o.toLowerCase() === value.trim().toLowerCase());
		if (match && !this.streams.includes(match)) {
			this.streams.push(match);
		}
		this.showStreamsDropdown = false;
		this.filteredStreams = [];
	}

	removeStream(index: number): void {
		this.streams.splice(index, 1);
	}

	onBranchInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.filteredBranches = this.filterOptions(this.branchOptions, value);
		this.showBranchesDropdown = this.filteredBranches.length > 0;
	}

	addBranch(value: string): void {
		const match = this.branchOptions.find(o => o.toLowerCase() === value.trim().toLowerCase());
		if (match && !this.coursesOffered.includes(match)) {
			this.coursesOffered.push(match);
		}
		this.showBranchesDropdown = false;
		this.filteredBranches = [];
	}

	removeBranch(index: number): void {
		this.coursesOffered.splice(index, 1);
	}

	onIndustryInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.filteredIndustries = this.filterOptions(this.industryOptions, value);
		this.showIndustriesDropdown = this.filteredIndustries.length > 0;
	}

	addIndustry(value: string): void {
		const match = this.industryOptions.find(o => o.toLowerCase() === value.trim().toLowerCase());
		if (match && !this.placementIndustries.includes(match)) {
			this.placementIndustries.push(match);
		}
		this.showIndustriesDropdown = false;
		this.filteredIndustries = [];
	}

	removeIndustry(index: number): void {
		this.placementIndustries.splice(index, 1);
	}

	// ─────────────────────────────────────────────
	// College name search-and-select
	// ─────────────────────────────────────────────

	get collegeNameControl(): FormControl {
		return this.registerForm.get('college_name') as FormControl;
	}

	selectInstitution(institution: CollegeItem): void {
		this.selectedInstitutionId = String(institution.id);
		const patchData: { city?: string; state?: string } = {};
		if (institution.city) {
			patchData.city = institution.city;
		}
		if (institution.state) {
			patchData.state = institution.state;
		}
		this.registerForm.patchValue(patchData, { emitEvent: false });
	}

	onCollegeSelectionCleared(): void {
		this.selectedInstitutionId = null;
	}

	onCantFindCollege(): void {
		const nameControl = this.registerForm.get('point_of_contact_name');
		const emailControl = this.registerForm.get('point_of_contact_email');
		nameControl?.markAsTouched();
		emailControl?.markAsTouched();

		if (nameControl?.invalid || emailControl?.invalid) {
			this.toastr.error('Please enter the point of contact name and email before adding a new college.');
			return;
		}

		// College Auth collects the POC as one combined "Contact Name" field,
		// but this modal (shared with other flows that collect first/last
		// name separately) expects them split. Split the same way the
		// backend does when it later creates the actual User record
		// (poc_name.strip().split(" ", 1) in college_portal_service.py) —
		// first word = first name, remainder = last name — so this pre-fill
		// always matches what registration will ultimately store, instead of
		// dumping the whole name into First Name with Last Name left blank.
		const fullName = (this.registerForm.value.point_of_contact_name || '').trim();
		const firstSpaceIndex = fullName.indexOf(' ');
		const firstName = firstSpaceIndex === -1 ? fullName : fullName.slice(0, firstSpaceIndex);
		const lastName = firstSpaceIndex === -1 ? '' : fullName.slice(firstSpaceIndex + 1).trim();

		const dialogRef = this._dialog.open(CantFindCollegeModalComponent, {
			data: {
				firstName,
				lastName,
				email: this.registerForm.value.point_of_contact_email
			}
		});

		dialogRef.afterClosed().subscribe((college: CollegeItem | undefined) => {
			if (!college) return;

			this.selectedInstitutionId = String(college.id);
			this.registerForm.patchValue({
				college_name: college.name,
				city: college.city || this.registerForm.value.city,
				state: college.state || this.registerForm.value.state
			}, { emitEvent: false });
		});
	}

	onPhoneInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const rawValue = input.value;
		const control = this.registerForm.get('phone_number');
		if (!control) return;
		control.markAsTouched();
		const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 10);
		input.value = digitsOnly;
		control.setValue(digitsOnly, { emitEvent: false });
	}

	// ─────────────────────────────────────────────
	// Logo upload — copied from CompanyEnrollmentComponent.onLogoSelect/removeLogo
	// ─────────────────────────────────────────────

	onLogoSelect(event: any): void {
		const file = event.target.files[0];
		if (!file) return;
		const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
		if (!allowedTypes.includes(file.type)) {
			this.toastr.error('Only PNG, JPG, SVG and WebP files are allowed');
			return;
		}
		if (file.size > 2 * 1024 * 1024) {
			this.toastr.error('File size should be less than 2MB');
			return;
		}
		this.selectedLogoFile = file;
		const reader = new FileReader();
		reader.onload = () => { this.logoPreview = reader.result as string; };
		reader.readAsDataURL(file);
	}

	removeLogo(event: Event): void {
		event.stopPropagation();
		this.selectedLogoFile = null;
		this.logoPreview = null;
	}

	// ─────────────────────────────────────────────
	// Registration submit
	// ─────────────────────────────────────────────

	onRegister(): void {
		if (this.registerForm.invalid) {
			this.registerForm.markAllAsTouched();
			return;
		}
		if (!this.selectedInstitutionId) {
			this.registerForm.get('college_name')?.markAsTouched();
			this.toastr.error('Please select your college from the list, or use "Can\'t find your college?" to add it.');
			return;
		}
		if (this.streams.length === 0) {
			this.toastr.error('Please add at least one stream.');
			return;
		}
		if (this.placementIndustries.length === 0) {
			this.toastr.error('Please add at least one placement industry.');
			return;
		}

		const raw = this.registerForm.value;

		// Profile fields go straight to the final /register call, held until
		// OTP verification succeeds — the identity fields below are sent to
		// send-otp instead (and are no longer part of /register's payload).
		const formData = new FormData();
		formData.append('college_name', (raw.college_name || '').trim());
		formData.append('college_url', (raw.college_url || '').trim());
		formData.append('university_name', (raw.university_name || '').trim());
		formData.append('ranking', (raw.ranking || '').trim());
		formData.append('city', (raw.city || '').trim());
		formData.append('state', (raw.state || '').trim());
		formData.append('country', raw.country || 'India');
		formData.append('institution_id', this.selectedInstitutionId as string);
		this.streams.forEach(s => formData.append('streams', s));
		this.coursesOffered.forEach(c => formData.append('courses_offered', c));
		this.placementIndustries.forEach(i => formData.append('placement_industries', i));
		if (this.selectedLogoFile) {
			formData.append('logo', this.selectedLogoFile);
		}
		this.pendingRegisterFormData = formData;

		this.sendRegistrationOtp();
	}

	sendRegistrationOtp(): void {
		const raw = this.registerForm.value;
		this.isLoading = true;
		this._collegePortalService.sendRegistrationOtp({
			college_name: (raw.college_name || '').trim(),
			point_of_contact_name: (raw.point_of_contact_name || '').trim(),
			point_of_contact_email: (raw.point_of_contact_email || '').trim(),
			phone_number: (raw.phone_number || '').trim(),
			password: raw.password,
		}).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (res) => {
				if (res.Success) {
					this.toastr.success(res.Message || 'OTP sent to your email. Please verify to continue.');
					this.registrationOtpTarget = res.Data?.point_of_contact_email || raw.point_of_contact_email;
					this.registrationOtpControl.setValue('');
					this.isVerifyingRegistrationOtp = true;
				} else {
					this.toastr.error(res.Message || 'Failed to send OTP. Please try again.');
				}
			},
			error: (err) => {
				this.toastr.error(err.error?.Message || 'Failed to send OTP. Please try again.');
			}
		});
	}

	verifyRegistrationOtp(): void {
		if (this.registrationOtpControl.invalid || !this.pendingRegisterFormData) {
			this.registrationOtpControl.markAsTouched();
			return;
		}

		this.isLoading = true;
		this._collegePortalService.verifyRegistrationOtp({
			point_of_contact_email: this.registrationOtpTarget,
			otp: parseInt(this.registrationOtpControl.value || '', 10),
		}).subscribe({
			next: (res) => {
				if (res.Success && res.Data?.otp_verification_token) {
					this.toastr.success(res.Message || 'OTP verified successfully.');
					this.pendingRegisterFormData!.append('otp_verification_token', res.Data.otp_verification_token);
					this.submitRegistration();
				} else {
					this.isLoading = false;
					this.toastr.error(res.Message || 'Invalid OTP.');
				}
			},
			error: (err) => {
				this.isLoading = false;
				const errMsg = err.error?.Message || 'Verification failed. Please try again.';
				const httpStatus = err.status;

				if (httpStatus === 404) {
					this.toastr.error('Registration session not found. Please restart registration.');
					this.isVerifyingRegistrationOtp = false;
					this.pendingRegisterFormData = null;
				} else if (httpStatus === 400) {
					this.toastr.error(errMsg);
					if (errMsg.toLowerCase().includes('expired')) {
						this.registrationOtpControl.setValue('');
					} else {
						this.registrationOtpControl.setErrors({ incorrect: true });
					}
				} else {
					this.toastr.error(errMsg);
				}
			}
		});
	}

	private submitRegistration(): void {
		const pocEmail = this.registerForm.value.point_of_contact_email;
		this._collegePortalService.registerCollege(this.pendingRegisterFormData!).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (res) => {
				if (res.Success) {
					this.toastr.success(res.Message || 'College registered successfully. Awaiting admin approval.');
					this.isVerifyingRegistrationOtp = false;
					this.pendingRegisterFormData = null;
					this.isLogin = true;
					this.loginForm.patchValue({ identifier: pocEmail });
				} else {
					this.toastr.error(res.Message || 'Registration failed. Please try again.');
				}
			},
			error: (err) => {
				this.toastr.error(err.error?.Message || 'Registration failed. Please try again.');
			}
		});
	}

	resendRegistrationOtp(): void {
		this.registrationOtpControl.setValue('');
		this.sendRegistrationOtp();
	}

	// Drops back to the form without resetting registerForm, so already-typed
	// fields (incl. the POC email that triggered the OTP) stay editable rather
	// than being retyped from scratch. The pending backend User/Tenant row for
	// the email the OTP was sent to isn't cleaned up here — if the user edits
	// the email to something else and resubmits, that earlier row is simply
	// left inactive with no CollegeAccount attached (harmless; picked back up
	// as a resend if they ever register with that original email again).
	changeRegistrationOtpTarget(): void {
		this.isVerifyingRegistrationOtp = false;
		this.pendingRegisterFormData = null;
		this.registrationOtpControl.setValue('');
	}

	// ─────────────────────────────────────────────
	// Login submit
	// ─────────────────────────────────────────────

	onLogin(): void {
		if (!this.isVerifyingOtp && this.loginMethod === 'otp') {
			this.sendOtp();
			return;
		}

		if (this.loginForm.invalid) {
			this.loginForm.markAllAsTouched();
			return;
		}

		const identifier = this.loginForm.value.identifier;
		const isPhone = /^[6-9]\d{9}$/.test(identifier);
		const payload: any = {
			is_default: false,
			login_source: 'web',
			fcm_token: ''
		};
		if (isPhone) {
			payload.phone = identifier;
		} else {
			payload.email = identifier;
		}

		if (this.isVerifyingOtp) {
			const otpValue = this.loginForm.get('otp')?.value;
			if (!otpValue) return;
			payload.otp = Number(otpValue);
		} else {
			payload.password = this.loginForm.value.password;
		}

		this.isLoading = true;
		this._authService.postLogin(payload).pipe(
			finalize(() => { this.isLoading = false; })
		).subscribe({
			next: (response) => {
				if (!response.Success) {
					this.toastr.error(response.Message);
					return;
				}

				const tokenStr = response.Data?.token || '';
				let isCollegeAdmin = false;
				if (tokenStr) {
					const decoded: any = jwtDecode(tokenStr);
					const permissions: string[] = decoded.permissions || [];
					isCollegeAdmin = permissions.includes('college_portal:read');
					if (!isCollegeAdmin) {
						this.toastr.error('You do not have access to the college portal.', 'Access Denied');
						return;
					}
				}

				this._authService.setToken(tokenStr);
				if (response.Data?.phone_email) {
					localStorage.setItem('phone_email', response.Data.phone_email);
				}
				if (response.Data?.refresh_token) {
					localStorage.setItem('refresh_token', response.Data.refresh_token);
				}
				if (response.Data?.modules) {
					this._authService.setModules(response.Data.modules);
				}

				const collegeStatus = response.Data?.college_status;
				if (collegeStatus === 'approved') {
					this.toastr.success('Login successful');
					this.router.navigate(['/college-dashboard/overview']);
				} else if (collegeStatus === 'blocked') {
					this.toastr.error('Your college account is blocked. Please contact support.');
				} else {
					this.toastr.error(
						'Your college registration is pending admin approval. You will be notified by email once approved.'
					);
				}
			},
			error: (err) => {
				this.toastr.error(err.error?.Message || 'Login failed. Please try again.');
			}
		});
	}
}
